import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import { Badge, EmptyState, PageLoading, TextArea } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import type { LeaveRequest, LeaveStatus } from '../../types';

/**
 * Leave requests for a manager or admin.
 *
 * A manager sees the requests routed to them; an admin sees the whole
 * company. Pending ones carry Approve / Reject; the rest are read-only
 * history. The server decides who may act on what.
 */
const TABS: { value: LeaveStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_TONE: Record<LeaveStatus, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral',
};

function fmtRange(from: string, to: string, halfDay: boolean): string {
  const f = (s: string) => new Date(`${s}T12:00:00Z`)
    .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  if (from === to) return halfDay ? `${f(from)} (half day)` : f(from);
  return `${f(from)} → ${f(to)}`;
}

export default function LeaveRequestsPage() {
  const toast = useToast();
  const [status, setStatus] = useState<LeaveStatus>('pending');
  const [rows, setRows] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<{ req: LeaveRequest; decision: 'approved' | 'rejected' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ requests: LeaveRequest[] }>('/leave/requests/pending', { status });
      setRows(res.requests);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load leave requests.');
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <PageHeader
        title="Leave requests"
        subtitle="Approve or decline the leave your team applies for."
      />

      <div className="page col gap-16">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.value} type="button"
              className={`tab${t.value === status ? ' active' : ''}`}
              onClick={() => setStatus(t.value)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <PageLoading /> : rows.length === 0 ? (
          <div className="card">
            <EmptyState icon="checklist"
              title={status === 'pending' ? 'Nothing to approve' : `No ${status} requests`}
              body={status === 'pending' ? 'Leave your team applies for shows up here.' : undefined} />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th className="num">Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    {status === 'pending' ? <th /> : <th>Decided by</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="primary">{r.user.name}</td>
                      <td>
                        <span className="row gap-8" style={{ alignItems: 'center' }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, flex: 'none',
                            background: r.leaveType?.color ?? 'var(--brand)' }} />
                          {r.leaveType?.name ?? '—'}
                        </span>
                      </td>
                      <td className="nowrap">{fmtRange(r.fromDate, r.toDate, r.halfDay)}</td>
                      <td className="num">{r.days}</td>
                      <td className="small">{r.reason ?? <span className="faint">—</span>}</td>
                      <td><Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge></td>
                      {status === 'pending' ? (
                        <td>
                          <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-primary btn-sm"
                              onClick={() => setDeciding({ req: r, decision: 'approved' })}>Approve</button>
                            <button type="button" className="btn btn-danger-ghost btn-sm"
                              onClick={() => setDeciding({ req: r, decision: 'rejected' })}>Reject</button>
                          </div>
                        </td>
                      ) : (
                        <td className="small">
                          {r.decidedBy?.name ?? '—'}
                          {r.decisionNote ? <div className="faint">{r.decisionNote}</div> : null}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {deciding ? (
        <DecisionModal
          request={deciding.req}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); void load(); }}
        />
      ) : null}
    </>
  );
}

function DecisionModal({ request, decision, onClose, onDone }: {
  request: LeaveRequest;
  decision: 'approved' | 'rejected';
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const approve = decision === 'approved';

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/leave/requests/${request.id}/decision`, { decision, note: note.trim() || undefined });
      toast.success(approve ? 'Leave approved.' : 'Leave declined.');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={approve ? 'Approve leave' : 'Decline leave'}
      subtitle={`${request.user.name} · ${fmtRange(request.fromDate, request.toDate, request.halfDay)} · ${request.days} day${request.days === 1 ? '' : 's'}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className={`btn ${approve ? 'btn-primary' : 'btn-danger'}`}
            disabled={busy} onClick={() => void submit()}>
            {approve ? 'Approve' : 'Decline'}
          </button>
        </>
      )}
    >
      <div className="col gap-8">
        {request.leaveType ? <p className="small faint" style={{ margin: 0 }}>Type: {request.leaveType.name}</p> : null}
        {request.reason ? <p className="small">“{request.reason}”</p> : null}
        <TextArea
          label={approve ? 'Note (optional)' : 'Reason for declining (optional)'}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}
